import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, TrendingUp, Briefcase, Target, Activity, FileText, BarChart3, Phone, Mail, Calendar, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react'
import { AddButton, ActionControls, Modal, ConfirmDialog, FormInput, FormSelect, FormTextarea } from '../components/common/CRUDControls'

export default function CustomerSalesPage() {
  const [stats, setStats] = useState({
    totalCustomers: 324,
    activePipeline: 1250000,
    conversionRate: 34,
    avgDealSize: 45000,
  })

  // Customer Management State
  const [customers, setCustomers] = useState([
    { id: 1, name: 'Acme Corp', email: 'contact@acme.com', phone: '555-0101', status: 'Active', value: '$125,000' },
    { id: 2, name: 'TechCorp Solutions', email: 'sales@techcorp.com', phone: '555-0102', status: 'Active', value: '$85,000' },
    { id: 3, name: 'GlobalSys Inc', email: 'info@globalsys.com', phone: '555-0103', status: 'Prospect', value: '$220,000' },
  ])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', status: 'Active', value: '' })

  const handleAddCustomer = () => {
    setFormData({ name: '', email: '', phone: '', status: 'Active', value: '' })
    setSelectedCustomer(null)
    setShowAddModal(true)
  }

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer)
    setFormData({ ...customer })
    setShowEditModal(true)
  }

  const handleDeleteCustomer = (customer) => {
    setSelectedCustomer(customer)
    setShowDeleteConfirm(true)
  }

  const handleSaveCustomer = () => {
    if (selectedCustomer) {
      setCustomers(customers.map(c => c.id === selectedCustomer.id ? { ...c, ...formData } : c))
      setShowEditModal(false)
    } else {
      setCustomers([...customers, { id: Date.now(), ...formData }])
      setShowAddModal(false)
    }
    setStats({ ...stats, totalCustomers: stats.totalCustomers + (selectedCustomer ? 0 : 1) })
  }

  const handleConfirmDelete = () => {
    setCustomers(customers.filter(c => c.id !== selectedCustomer.id))
    setShowDeleteConfirm(false)
    setStats({ ...stats, totalCustomers: stats.totalCustomers - 1 })
  }

  const crmFeatures = [
    {
      title: '👥 Customer Management',
      description: 'Manage all your customers, contacts, and company information',
      icon: Users,
      color: 'from-blue-600 to-blue-500',
      stats: '324 Customers',
      path: '/customers'
    },
    {
      title: '📊 Sales Pipeline',
      description: 'Track deals through your sales stages and manage opportunities',
      icon: TrendingUp,
      color: 'from-cyan-600 to-cyan-500',
      stats: '42 Active Deals',
      path: '/sales-pipeline'
    },
    {
      title: '🎯 Lead Management',
      description: 'Capture, score, and nurture leads to convert into customers',
      icon: Target,
      color: 'from-indigo-600 to-indigo-500',
      stats: '187 Leads',
      path: '/leads'
    },
    {
      title: '💼 Opportunities',
      description: 'Track sales opportunities and deals throughout the sales process',
      icon: Briefcase,
      color: 'from-teal-600 to-teal-500',
      stats: '$1.25M Pipeline',
      path: '/opportunities'
    },
    {
      title: '📞 Activities & Follow-ups',
      description: 'Log calls, emails, meetings, and schedule follow-up tasks',
      icon: Activity,
      color: 'from-purple-600 to-purple-500',
      stats: '156 Scheduled',
      path: '/activities'
    },
    {
      title: '📋 Contact Management',
      description: 'Organize and manage all customer and prospect contacts',
      icon: Phone,
      color: 'from-pink-600 to-pink-500',
      stats: '892 Contacts',
      path: '/contacts'
    },
    {
      title: '📈 Sales Forecasting',
      description: 'Predict revenue and identify at-risk deals with AI-powered forecasting',
      icon: BarChart3,
      color: 'from-amber-600 to-amber-500',
      stats: 'Q2: $425K',
      path: '/sales-forecasting'
    },
    {
      title: '📊 Reports & Analytics',
      description: 'Advanced sales analytics, win rates, cycle time, and performance metrics',
      icon: FileText,
      color: 'from-orange-600 to-orange-500',
      stats: '34% Close Rate',
      path: '/sales-reports'
    },
  ]

  const pipelineStages = [
    { name: 'Lead', count: 187, value: 93500, color: 'from-blue-500 to-blue-600' },
    { name: 'Prospect', count: 85, value: 382500, color: 'from-cyan-500 to-cyan-600' },
    { name: 'Negotiation', count: 34, value: 459000, color: 'from-indigo-500 to-indigo-600' },
    { name: 'Proposal', count: 28, value: 315000, color: 'from-purple-500 to-purple-600' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Briefcase className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Customer Sales CRM</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Manage Your Complete Sales Pipeline & Customer Relationships</p>
                <p className="text-blue-200 text-sm mt-1">AI-Powered Sales Intelligence & Automation</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">${stats.activePipeline.toLocaleString().split(',')[0]}K</div>
                <div className="text-sm text-blue-100">Active Pipeline</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Customers', value: stats.totalCustomers, icon: Users, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Active Pipeline', value: `$${(stats.activePipeline / 1000).toFixed(0)}K`, icon: TrendingUp, color: 'from-cyan-600 to-cyan-500', textColor: 'text-cyan-400' },
            { label: 'Conversion Rate', value: `${stats.conversionRate}%`, icon: CheckCircle2, color: 'from-indigo-600 to-indigo-500', textColor: 'text-indigo-400' },
            { label: 'Avg Deal Size', value: `$${(stats.avgDealSize / 1000).toFixed(0)}K`, icon: Briefcase, color: 'from-teal-600 to-teal-500', textColor: 'text-teal-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Sales Pipeline Stages */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
            <TrendingUp className="w-6 h-6 text-blue-400" />
            <span>Sales Pipeline Stages</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {pipelineStages.map((stage, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg p-4 border border-blue-500/20">
                <div className={`h-1 w-full bg-gradient-to-r ${stage.color} rounded-full mb-4`}></div>
                <h3 className="text-white font-bold mb-3">{stage.name}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-300 text-sm">Deals</span>
                    <span className="text-white font-bold">{stage.count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-300 text-sm">Value</span>
                    <span className="text-cyan-400 font-bold">${(stage.value / 1000).toFixed(0)}K</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CRM Features Grid */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Briefcase className="w-7 h-7 text-blue-400" />
            <span>CRM Tools & Features</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {crmFeatures.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <Link
                  key={idx}
                  to={feature.path}
                  className="group relative flex flex-col overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1 h-full no-underline"
                  style={{ textDecoration: 'none' }}
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>

                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="font-bold text-white mb-2 text-lg flex-shrink-0">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4 flex-grow">{feature.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20 flex-shrink-0">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-blue-400" />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activities */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Activity className="w-5 h-5 text-blue-400" />
              <span>Recent Activities</span>
            </h3>
            <div className="space-y-3">
              {[
                { action: 'Call with Acme Corp', time: '2 hours ago', icon: Phone, color: 'text-blue-400' },
                { action: 'Email sent to prospect', time: '4 hours ago', icon: Mail, color: 'text-cyan-400' },
                { action: 'Meeting scheduled', time: '1 day ago', icon: Calendar, color: 'text-indigo-400' },
                { action: 'Deal moved to proposal', time: '2 days ago', icon: CheckCircle2, color: 'text-teal-400' },
              ].map((activity, idx) => {
                const Icon = activity.icon
                return (
                  <div key={idx} className="flex items-center space-x-3 pb-3 border-b border-blue-500/20 last:border-b-0">
                    <Icon className={`w-4 h-4 ${activity.color}`} />
                    <div className="flex-1">
                      <p className="text-white text-sm font-semibold">{activity.action}</p>
                      <p className="text-blue-400 text-xs">{activity.time}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* At-Risk Deals */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <AlertCircle className="w-5 h-5 text-orange-400" />
              <span>At-Risk Deals</span>
            </h3>
            <div className="space-y-3">
              {[
                { deal: 'TechCorp - $125K', reason: 'No contact in 7 days', impact: 'High' },
                { deal: 'InnovateLabs - $85K', reason: 'Budget concerns', impact: 'Medium' },
                { deal: 'GlobalSys - $220K', reason: 'Competitor activity', impact: 'High' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-orange-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-semibold text-sm">{item.deal}</p>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      item.impact === 'High' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'
                    }`}>
                      {item.impact} Impact
                    </span>
                  </div>
                  <p className="text-blue-300 text-xs">{item.reason}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Manage Customers */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
              <Users className="w-6 h-6 text-blue-400" />
              <span>Manage Customers</span>
            </h2>
            <AddButton onClick={handleAddCustomer} label="Add Customer" />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Phone</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Value</th>
                  <th className="text-center py-3 px-4 text-blue-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                    <td className="py-3 px-4 text-white font-semibold">{customer.name}</td>
                    <td className="py-3 px-4 text-blue-300">{customer.email}</td>
                    <td className="py-3 px-4 text-blue-300">{customer.phone}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        customer.status === 'Active' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-cyan-400 font-semibold">{customer.value}</td>
                    <td className="py-3 px-4 flex items-center justify-center">
                      <ActionControls
                        onEdit={() => handleEditCustomer(customer)}
                        onDelete={() => handleDeleteCustomer(customer)}
                        size="lg"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Customer Modal */}
        <Modal
          isOpen={showAddModal || showEditModal}
          onClose={() => {
            setShowAddModal(false)
            setShowEditModal(false)
          }}
          title={selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
          onSubmit={handleSaveCustomer}
          submitLabel={selectedCustomer ? 'Update' : 'Add'}
        >
          <FormInput
            label="Customer Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Acme Corporation"
            required
          />
          <FormInput
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="contact@example.com"
            required
          />
          <FormInput
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="555-0000"
          />
          <FormSelect
            label="Status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Prospect', label: 'Prospect' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
          />
          <FormInput
            label="Deal Value"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            placeholder="e.g., $125,000"
          />
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          title="Delete Customer"
          message={`Are you sure you want to delete ${selectedCustomer?.name}? This action cannot be undone.`}
        />

      </div>
    </div>
  )
}
